/**
 * TagQualityRing — SVG arc ring showing metadata completeness.
 *
 * Renders a small circular progress ring whose filled arc represents
 * what fraction of the document's metadata tags have been populated
 * (non-null). Used in the HubDetail "Tags" section header to give
 * the writer an at-a-glance quality signal before they expand the
 * tag list.
 *
 *   1.0      → evidenceHigh (emerald)
 *   ≥ 0.6    → evidenceMid  (amber)
 *   < 0.6    → evidenceLow  (red)
 *
 * Returns null when total === 0 (no tags to fill).
 *
 * Implementation: two SVG circles with stroke-dasharray / offset.
 * The ring starts at 12 o'clock (−90° rotation).
 */

import type React from "react";
import { aiColors } from "../tokens";

export interface TagQualityRingProps {
  filled: number;
  total: number;
  /** Outer diameter in pixels. Defaults to 20. */
  size?: number;
}

function ringColor(filled: number, total: number): string {
  if (total === 0) return "#D1D5DB";
  const ratio = filled / total;
  if (ratio >= 1.0) return aiColors.evidenceHigh;
  if (ratio >= 0.6) return aiColors.evidenceMid;
  return aiColors.evidenceLow;
}

export const TagQualityRing: React.FC<TagQualityRingProps> = ({ filled, total, size = 20 }) => {
  if (total === 0) return null;

  const strokeWidth = 2;
  const r = (size - strokeWidth * 2) / 2;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const filledArc = total > 0 ? (filled / total) * circumference : 0;
  const color = ringColor(filled, total);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`${filled} of ${total} tags populated`}
      role="img"
      style={{ flexShrink: 0 }}
    >
      {/* Track */}
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
      {/* Filled arc */}
      {filled > 0 && (
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${filledArc} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cx})`}
        />
      )}
    </svg>
  );
};

export default TagQualityRing;
